import ThdfQrLookup from "./ThdfQrLookup";
import React from "react";
import { createPortal } from "react-dom";
import { X, XCircle } from "lucide-react";

import DocumentScanner from "./scanners/DocumentScanner";
import {
  CLEARANCE_HINTS,
  formatDateTime,
  parseClearancePayload,
  resolveStatusPresentation,
} from "../pages/QRScanner";

type MenubarQrScannerModalProps = {
  open: boolean;
  onClose: () => void;
};

type ScanState = {
  raw: string;
  error: string | null;
  result: ReturnType<typeof parseClearancePayload>["result"] | null;
};

function createInitialState(): ScanState {
  return {
    raw: "",
    error: null,
    result: null,
  };
}

export default function MenubarQrScannerModal({ open, onClose }: MenubarQrScannerModalProps) {
  const [scanState, setScanState] = React.useState<ScanState>(createInitialState);

  const handleScan = React.useCallback((payload: string) => {
    const raw = (payload ?? "").toString();
    const { result, error } = parseClearancePayload(raw);
    if (error) {
      setScanState({ result: null, error, raw });
      return;
    }
    setScanState({ result, error: null, raw });
  }, []);

  React.useEffect(() => {
    if (!open) {
      setScanState(createInitialState());
    }
  }, [open]);

  const statusPresentation = resolveStatusPresentation(scanState.result?.status);
  const subjectLabel = scanState.result?.subjectLabel || "Subject";
  const detailFields = React.useMemo(() => {
    if (!scanState.result) return [];
    const r = scanState.result;
    const entries: Array<{ label: string; value: string }> = [];
    const subjectValue = r.subjectName || r.travellerName || r.id || "—";
    entries.push({ label: subjectLabel, value: subjectValue });
    if (r.travellerGender) entries.push({ label: "Gender", value: r.travellerGender });
    if (r.passportNumber) entries.push({ label: "Passport/ID", value: r.passportNumber });
    if (r.nationality) entries.push({ label: "Nationality", value: r.nationality });
    if (r.certificateNumber) entries.push({ label: "Certificate #", value: r.certificateNumber });
    if (r.clearanceId || r.id) entries.push({ label: "Record ID", value: r.clearanceId || r.id });
    if (r.portOfEntry) entries.push({ label: "Port of entry", value: r.portOfEntry });
    if (r.issuedBy) entries.push({ label: "Issuing officer", value: r.issuedBy });
    if (r.issuedByRole) entries.push({ label: "Officer role", value: r.issuedByRole });
    if (r.issuedById) entries.push({ label: "Officer ID", value: r.issuedById });
    if (r.issuedDate) entries.push({ label: "Issued at", value: formatDateTime(r.issuedDate) });
    (r.extras || []).forEach((entry: { label?: string; value?: unknown }) => {
      if (!entry || !entry.label) return;
      const value = entry.value;
      const stringValue =
        value == null
          ? "—"
          : typeof value === "string"
            ? value
            : typeof value === "number"
              ? value.toString()
              : typeof value === "boolean"
                ? value.toString()
                : JSON.stringify(value);
      entries.push({ label: entry.label, value: stringValue || "—" });
    });
    return entries;
  }, [scanState.result, subjectLabel]);

  if (!open) {
    return null;
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-900/70 px-4 py-10"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative w-full max-w-5xl overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Scan QR</h2>
            <p className="mt-1 text-sm text-slate-500">
              Use the device camera or paste the QR payload to quickly verify a Port Health document (clearance, detention, cargo, etc.).
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-100"
          >
            <X className="h-4 w-4" />
            Close
          </button>
        </div>

        <div className="grid gap-6 px-6 py-6 lg:grid-cols-[320px_minmax(0,1fr)]">
          <div className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <DocumentScanner
                onScan={handleScan}
                documentTypeLabel="Port Health QR"
                hints={CLEARANCE_HINTS}
                startButtonLabel="Start scan"
              />
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <h3 className="text-sm font-semibold text-slate-700">Raw payload</h3>
              {scanState.raw ? (
                <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-xl bg-slate-900/5 p-3 text-xs text-slate-700">
                  {scanState.raw}
                </pre>
              ) : (
                <p className="mt-2 text-xs text-slate-500">
                  Raw QR contents will appear here after a scan.
                </p>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <h3 className="text-base font-semibold text-slate-700">Verification result</h3>

              {!scanState.result && !scanState.error && (
                <div className="mt-4 rounded-xl border border-dashed border-slate-300 bg-slate-50 p-6 text-sm text-slate-500">
                  Start a scan to view the traveller summary and exit decision cues.
                </div>
              )}

              {scanState.error && (
                <div className="mt-4 space-y-3 rounded-xl border border-rose-200 bg-rose-50 p-5 text-sm text-rose-700">
                  <div className="flex items-center gap-2 font-semibold">
                    <XCircle className="h-5 w-5" />
                    Verification failed
                  </div>
                  <p>{scanState.error}</p>
                  <div className="rounded-lg bg-white/70 p-3 text-xs text-rose-600">
                    <p className="font-semibold text-rose-700">Troubleshooting tips</p>
                    <ul className="mt-1 list-disc space-y-1 pl-5">
                      <li>Confirm the QR belongs to a Port Health document and not a laboratory or vaccination document.</li>
                      <li>Check that the issuing officer reprinted the document after the latest update.</li>
                      <li>Re-scan using a brighter light source to avoid truncated payloads.</li>
                    </ul>
                  </div>
                </div>
              )}

              {scanState.result?.payloadType === "traveller.thdfDeclaration" && (
                <ThdfQrLookup key={scanState.result.id} id={scanState.result.id} onOpen={onClose} />
              )}

              {scanState.result && scanState.result.payloadType !== "traveller.thdfDeclaration" && (
                <div className="mt-4 space-y-5">
                  <div
                    className={`flex items-center justify-between rounded-xl border px-4 py-3 text-sm font-semibold ${statusPresentation.badgeClass}`}
                  >
                    <div className="flex items-center gap-2">
                      {statusPresentation.icon}
                      <span>{statusPresentation.label}</span>
                    </div>
                    <span className="text-xs font-medium">Version v{scanState.result.version}</span>
                  </div>
                  <p className="text-sm text-slate-600">{statusPresentation.message}</p>
                  <dl className="grid grid-cols-1 gap-4 text-sm text-slate-700 md:grid-cols-2">
                    {detailFields.map((field) => (
                      <div key={`${field.label}-${field.value}`}>
                        <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          {field.label}
                        </dt>
                        <dd className="mt-1 font-medium text-slate-800">{field.value || "—"}</dd>
                      </div>
                    ))}
                    <div className="md:col-span-2">
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Remarks</dt>
                      <dd className="mt-1 text-sm text-slate-600">
                        {scanState.result.remarks ? scanState.result.remarks : "No additional remarks."}
                      </dd>
                    </div>
                  </dl>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
